import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import MemberHeader from './MemberHeader';
import MemberSidebar from './MemberSidebar';

// Import extracted views
import PlanView from './plan/PlanView';
import WorkView from './work/WorkView';
import VaultView from './vault/VaultView';
import IdentityView from './identity/IdentityView';

import { useAuth } from '../../contexts/AuthContext';
import { tasksService, Task as ApiTask } from '../../services/tasksService';

// --- Mock Data (Centralized here for now, passed as props) ---

const MOCK_USER = {
    name: "Arjun Verma",
    empId: "EMP-2024-889",
    email: "arjun.v@anygrc.com",
    dept: "Operations - South",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Arjun",
    postings: [
        { id: 1, title: "Branch Manager", geography: "Hyderabad Branch", type: "Operational" },
        { id: 2, title: "Safety Committee Member", geography: "South Zone", type: "Committee" }
    ]
};

const MOCK_SCOPE = {
    sops: [
        { id: 1, name: "Cash Handling SOP", role: "Owner", description: "Standard procedures for vault access and cash transit.", frequency: "Annual Review", lastUpdated: "2 days ago" },
        { id: 2, name: "Visitor Entry Protocol", role: "Approver", description: "Guidelines for non-staff entry and ID verification.", frequency: "Bi-Annual Review", lastUpdated: "1 week ago" }
    ],
    risks: [
        { id: 1, name: "Theft/Burglary Risk", level: "High", role: "Accountable", mitigation: "CCTV, Security Guards" },
        { id: 2, name: "Operational Downtime", level: "Medium", role: "Consulted", mitigation: "Backup Generators, Offline Mode" }
    ],
    compliances: [
        { id: 1, name: "GST Compliance (State)", role: "Executor", frequency: "Monthly", authority: "State Commercial Tax Dept" },
        { id: 2, name: "Fire Safety Audit Certification", role: "Responsible", frequency: "Quarterly", authority: "Municipal Corporation" },
        { id: 3, name: "Shops & Establishments Act", role: "Executor", frequency: "Annual", authority: "Labor Dept" }
    ]
};

// --- Mapping helpers: real Task -> WorkView's task display shape ---

// Map UTL_entity_registry source type codes to the persona-facing
// "type" pill used by WorkView. Anything not in this list falls back
// to TASK so the existing icon/colour logic still renders.
const SOURCE_TYPE_TO_PILL: Record<string, string> = {
    CAPA_ISSUE: 'CAPA',
    AUDIT_PROGRAM: 'AUDIT',
    AUDIT_FINDING: 'AUDIT',
    OBLIGATION: 'COMPLIANCE',
    FILING: 'COMPLIANCE',
    INCIDENT: 'INCIDENT',
    AGENDA_ITEM: 'MEETINGS',
    MEETING: 'MEETINGS',
    PROJECT: 'PROJECT',
};

const STATUS_TO_PILL: Record<string, string> = {
    OPEN: 'Pending',
    IN_PROGRESS: 'In Progress',
    DONE: 'Done',
    VERIFIED: 'Verified',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
};

const PRIORITY_TO_PILL: Record<string, string> = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    CRITICAL: 'Critical',
};

const adaptTaskForWorkView = (t: ApiTask) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const overdue = t.due_date && new Date(t.due_date) < today
        && !['DONE', 'VERIFIED', 'REJECTED', 'CANCELLED'].includes(t.status);
    return {
        id: t.id,
        type: SOURCE_TYPE_TO_PILL[t.source_type_code || ''] || 'TASK',
        title: t.name,
        dueDate: t.due_date || '',
        status: overdue ? 'Overdue' : (STATUS_TO_PILL[t.status] || t.status),
        priority: PRIORITY_TO_PILL[t.priority || ''] || (t.priority || 'Medium'),
        context: t.linked_label || t.source_label || '',
        description: t.description || '',
    };
};

// --- Main Component ---

const MemberDashboard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeView = searchParams.get('view') || 'WORK';
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const { state: authState } = useAuth();
    const myUserId = (authState.user as any)?.id;

    const [myTasks, setMyTasks] = useState<ApiTask[]>([]);
    useEffect(() => {
        if (!myUserId) return;
        tasksService.list({ mine: true, limit: 200 })
            .then(setMyTasks)
            .catch(() => setMyTasks([]));
    }, [myUserId]);

    const adaptedTasks = useMemo(() => myTasks.map(adaptTaskForWorkView), [myTasks]);

    const setActiveView = (view: string) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('view', view);
            // Reset tab when switching views if needed, or keep it if relevant
            newParams.delete('tab');
            return newParams;
        });
    };

    return (
        <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
            <MemberHeader onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

            <div className="member-layout flex-1">
                <MemberSidebar activeView={activeView} onViewChange={setActiveView} isOpen={isSidebarOpen} />

                <main className="member-main-content">
                    <div className="member-dashboard-container p-6 overflow-y-auto">
                        <div className="max-w-7xl mx-auto h-full">
                            {activeView === 'PLAN' && <PlanView />}

                            {activeView === 'WORK' && (
                                <WorkView
                                    tasks={adaptedTasks}
                                    scope={MOCK_SCOPE}
                                    workScope="active"
                                />
                            )}

                            {activeView === 'HISTORY' && (
                                <WorkView
                                    tasks={adaptedTasks}
                                    scope={MOCK_SCOPE}
                                    workScope="archive"
                                />
                            )}

                            {activeView === 'VAULT' && <VaultView />}

                            {activeView === 'IDENTITY' && <IdentityView user={MOCK_USER} scope={MOCK_SCOPE} />}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MemberDashboard;